import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  CreateFinanceCustomCategoryDocument,
  CustomCategoriesPageDocument,
  FinanceCategoryLedgerAccountInput,
  FinanceCustomCategoryItemFragment,
  FinanceCustomCategoryLegalEntityFragment,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import SelectField from 'modules/core/components/SelectField'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import LedgerAccountAssigmentForm from 'modules/finance/components/CustomCategories/LedgerAccountAssignmentForm'
import { useLedgerAccountAssignment } from 'modules/finance/hooks/accounting/useLedgerAccountAssignment'

interface CreateCustomCategoryForm {
  label: string
  parentCategoryId: string
  assignedLedgerAccounts: FinanceCategoryLedgerAccountInput[]
}

interface Props {
  parentCategories: FinanceCustomCategoryItemFragment[]
  parentCategoryId?: string
  legalEntities: FinanceCustomCategoryLegalEntityFragment[]
  canEditLedgerAccountAssignments: boolean
  onClose: () => void
}

const CreateCustomCategoryModal = ({
  parentCategories,
  parentCategoryId,
  legalEntities,
  canEditLedgerAccountAssignments,
  onClose,
}: Props) => {
  const { company } = useCurrentSession()

  const [createCustomCategory] = useMutation(CreateFinanceCustomCategoryDocument, {
    extractorFn: (data) => data.financeMutations.createFinanceCustomCategory,
  })

  const parentCategoryOptions = parentCategories.map((category) => ({
    label: category.label,
    value: category.id,
  }))

  const form = useForm<CreateCustomCategoryForm>({
    defaultValues: {
      label: '',
      parentCategoryId: parentCategoryId?.toString() ?? '',
      assignedLedgerAccounts: [],
    },
    validate: () => {
      const errors: FormErrors<CreateCustomCategoryForm> = {}
      return errors
    },
    onSubmit: async (data, { setErrors }) => {
      const result = await createCustomCategory({
        variables: {
          companyId: company.id,
          label: data.label,
          parentCategoryId: String(data.parentCategoryId),
          enabled: true,
          protected: false,
          visible: true,
          ledgerAccountAssignments: data.assignedLedgerAccounts,
        },
        refetchQueries: [CustomCategoriesPageDocument],
      }).then((res) => {
        track(trackingEvents.accounting.settings.categories.addSubcategory)
        return res
      })

      const handled = handleResult(result, (errors) => {
        setErrors(errors)
        app.setFlash({
          type: 'wrong',
          message: errors.messages || i18n.t('errors.generic_error'),
        })
      })
      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })

        handleClose()
      }
      return handled
    },
  })

  const handleSelectLedgerAccount = useLedgerAccountAssignment(form.setValues)

  const handleSave = async () => {
    form.handleSubmit()
  }

  const handleClose = () => {
    onClose()
  }

  return (
    <Modal
      illustration="document-edit"
      title={i18n.t('finance.settings.accounting.custom_categories.modals.create.title')}
      footer={
        <>
          <Button type="secondary" onClick={handleClose}>
            {i18n.t('finance.settings.accounting.custom_categories.modals.buttons.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={
              form.isSubmitting ||
              !form.isValid ||
              !form.dirty ||
              !form.values.parentCategoryId ||
              !form.values.label
            }
          >
            {i18n.t('finance.settings.accounting.custom_categories.modals.buttons.add')}
          </Button>
        </>
      }
      onClose={onClose}
    >
      <Box gap="s24">
        <TextField
          label={i18n.t('finance.settings.accounting.custom_categories.modals.create.fields.name')}
          onChange={(value) => form.setValues({ ...form.values, label: value })}
          value={form.values.label}
          error={form.errors.label}
          mandatory
        />
        <SelectField
          label={i18n.t(
            'finance.settings.accounting.custom_categories.modals.create.fields.parent'
          )}
          options={parentCategoryOptions}
          onChange={(value) => form.setValues({ ...form.values, parentCategoryId: value })}
          value={form.values.parentCategoryId}
          none={i18n.t('common.select')}
          error={form.errors.parentCategoryId}
          mandatory
        />
        {canEditLedgerAccountAssignments && (
          <LedgerAccountAssigmentForm
            legalEntities={legalEntities}
            onChange={handleSelectLedgerAccount}
          />
        )}
      </Box>
    </Modal>
  )
}

export default CreateCustomCategoryModal
